import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ICustomTinConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  getPopulateRelativeToRootFieldConfig,
  getPopulatesPropsSettingsConfigFormly,
  getUsePrefetchFieldConfig,
  populatesPropsParseIntoSettingsFormModel,
  populatesPropsUpdateFromSettingsFormModel,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  parseInvalidTinValidatorParseFieldIntoModel,
  parseRequiredValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setInvalidTinFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { tinResponseProps } from '../../../models/custom-tin-input-responses.model';
import {
  getRequiredValidatorFormConfig,
  getUniqueFieldValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import {
  parseConditionalValidatorsModelFromFieldIntoModel,
  parseUtilValidatorsModelFromFieldIntoModel,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import {
  setConditionalExpressionFromModel,
  setUtilConditionalExpressionFromModel,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';
import {
  getConditionalUtilValidatorsFormConfig,
  getConditionalValidationsFormConfig,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-generator.util';
import { EAvailableConditionalValidations } from '../../../models/form-fields-formly-conditional-validation.model';

export const tinInputGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        endpointCode: 'FTINDETAILS',
      },
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...getTinFieldSettingsConfig(),
        getUsePrefetchFieldConfig(formBuilderServiceInstance),
        getPopulateRelativeToRootFieldConfig(),
        ...getPopulatesPropsSettingsConfigFormly(formBuilderServiceInstance, [
          ...Array.from(tinResponseProps),
        ]),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getUniqueFieldValidatorFormConfig(formBuilderServiceInstance),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
        getConditionalValidationsFormConfig(
          [EAvailableConditionalValidations.required],
          formBuilderServiceInstance
        ),
        getConditionalUtilValidatorsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const tinFieldParseIntoSettingsFormModel = (
  tinField: FormlyFieldConfig
): ICustomTinConfigurationFormModel => {
  const model: ICustomTinConfigurationFormModel =
    populatesPropsParseIntoSettingsFormModel(tinField);

  model.tinFieldContentConfig = {
    endpointCode: tinField.props?.['endpointCode'],
    mixpanelEventAnalytics: tinField.props?.['mixpanelEventAnalytics'],
  };

  return model;
};

export const tinFieldUpdateFromSettingsFormModel = (
  model: ICustomTinConfigurationFormModel,
  tinField: FormlyFieldConfig
): FormlyFieldConfig => {
  tinField = baseFieldUpdateFromSettingsFormModel(model, tinField);
  tinField = populatesPropsUpdateFromSettingsFormModel(model, tinField);

  const tinFieldProps: Record<string, any> = tinField?.props
    ? { ...tinField?.props }
    : {};
  tinFieldProps['usePrefetch'] = model.usePrefetch;
  tinFieldProps['endpointCode'] = model.tinFieldContentConfig?.endpointCode;
  tinFieldProps['mixpanelEventAnalytics'] =
    model.tinFieldContentConfig?.mixpanelEventAnalytics;

  tinField.props = { ...tinFieldProps };

  return tinField;
};

export const tinFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseInvalidTinValidatorParseFieldIntoModel(model, field);
  model = parseConditionalValidatorsModelFromFieldIntoModel(model, field);
  model = parseUtilValidatorsModelFromFieldIntoModel(model, field);

  return model;
};

export const tinFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  tinInput: FormlyFieldConfig
): FormlyFieldConfig => {
  tinInput = setRequiredValidatorUpdateFieldFromModel(model, tinInput);
  tinInput = setInvalidTinFormDataIntoField(model, tinInput);
  tinInput = setConditionalExpressionFromModel(model, tinInput);
  tinInput = setUtilConditionalExpressionFromModel(model, tinInput);

  return tinInput;
};

export const tinFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const tinFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getTinFieldSettingsConfig = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'tinFieldContentConfig',
      id: 'tinFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        dragDisabled: false,
      },
      fieldGroup: [
        {
          key: 'endpointCode',
          type: 'input',
          defaultValue: 'FTINDETAILS',
          props: {
            label: 'Endpoint Code',
            required: true,
            placeholder: 'Enter TIN Fetch endpoint code',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'mixpanelEventAnalytics',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: false,
          props: { label: 'Enable MixPanel Analytics', formCheck: 'switch' },
        },
      ],
    },
  ];
};
